/* eslint-disable no-magic-numbers */
import {
  Calculator,
  Chance,
  LoadUtil,
  TargettedEvent,
  UIUtil,
} from '@suntex.core/animation';
import { LogLevel, Logger } from '@suntex.core/logging';
import {
  Client,
  Entities_Player_Progress_Response,
  Entities_Player_UpdateProgress_Request,
  Entities_Player_UpdateProgress_Response,
} from '@suntex.fim/api-client';

import { Scoreboard } from './Scoreboard';
import {
  EndScreenSymbol,
  GameFrameButton,
  GameFrameExportRoot,
  GameFrameParams,
  GameFrameSymbol,
  PracticeMenuSymbol,
} from './types';

const logger = Logger.for(['GameFrame'], { level: LogLevel.Debug });

const DEFAULT_SCORE_MAX = 360;
const DEFAULT_ROUNDS_IN_MODULE = 4;
const DEFAULT_GAMES_PER_ROUND = 15;
const DEFAULT_BONUS_MAX = -1;
const ANIM_MS = 500;
const INTERACTION_TICK_MS = 30000;
const INTERACTION_TICK_SECONDS = 30;
const POINTS_LOW = 50;
const POINTS_MED = 150;
const POINTS_HIGH = 300;
const STICKERS_LOW_MIN = 1;
const STICKERS_LOW_MAX = 5;
const ROUND_BTN_OFFSET = 50;

type GameData = {
  round: number;
  game: number;
  loginId?: number;
  moduleId?: number;
  extendedData?: string;
  finishedEarning?: boolean;
};

/**
 * A modern, per-instance replacement for the legacy global `GameFrame`
 * symbol described in `@suntex.classic/symbols/GameFrame.ts`.
 *
 * Returned object carries lifecycle, score/bonus, scoreboard wiring, end
 * screen, and progress-fetch/update logic. Built once per module instance.
 */
export interface GameFrame {
  /** Currently-instantiated FLA `gameFrame` symbol. */
  readonly symbol: GameFrameSymbol;
  readonly scoreboard: Scoreboard;
  readonly endScreen: EndScreenSymbol;
  readonly practiceMenu: PracticeMenuSymbol;
  readonly changeRoundBtn: createjs.Container;
  readonly veil: createjs.DisplayObject;
  readonly gameStage: createjs.Container;

  /** Module identity (read-only after construction). */
  readonly moduleId: number;
  moduleVariant: number;
  preview: boolean;
  pwaMode: boolean;

  /** Score / round state (mutable; modules read these directly). */
  round: number;
  game: number;
  score: number;
  scoreMax: number;
  bonus: number;
  bonusMax: number;
  timeOnTask: number;
  roundsInModule: number;
  gamesPerRound: number;

  /** Optional extra payload round-tripped through `Entities_Player_Progress_Response.extendedData`. */
  extendedData?: string;

  /** When false, modules manage scoreboard updates themselves. */
  automateScoreboard: boolean;
  /** When false, modules call `endGame()` without auto-posting progress. */
  automateDatabaseUpdates: boolean;
  /** When true, end-screen renders points text instead of stickers text. */
  displayPoints: boolean;
  disableNextBtn: boolean;
  disableMoreBtn: boolean;

  /** Last loaded vars from `getProgress`. */
  loadedVars: GameData | null;

  /** Wire button handlers on the FLA gameFrame symbol. */
  init(): void;

  /** Register start/stop callbacks then either auto-fetch progress or
   *  immediately enter preview mode. */
  initGame(startGameFn: () => void, stopGameFn?: () => void): void;

  startGame(): void;
  endGame(): void;
  nextGame(): void;
  finished(): boolean;
  getLevel(): number;

  setScore(score: number): void;
  incrementScore(by?: number): void;
  incrementBonus(by: number): void;
  updateScoreboard(): void;

  setGameData(vars: GameData): void;
  resetRoundProgress(): void;

  loadSound(src: string, id: string): void;
  setBackgroundLoop(src: string): void;
  mute(): void;
}

export function createGameFrame({
  api,
  exportRoot,
  params,
}: {
  api: Client;
  exportRoot: GameFrameExportRoot;
  params: GameFrameParams;
}): GameFrame {
  const symbol = exportRoot.gameFrame;
  const gameStage = exportRoot.gameStage;

  const scoreboard = new Scoreboard(symbol.scoreboard);
  const endScreen = symbol.endScreen;
  const practiceMenu = symbol.practiceMenu;
  const changeRoundBtn = symbol.changeRoundBtn;
  const veil = symbol.veil;

  const soundDirectory = params.soundDirectory ?? '/sounds/';

  const getProgress: () => Promise<Entities_Player_Progress_Response> =
    params.getProgress ??
    (() =>
      api.get_Player_Progress({
        playerId: params.playerId!,
        moduleId: params.moduleId,
        moduleVariant: params.moduleVariant ?? 0,
      }));

  const updateProgress: (
    vars: Entities_Player_UpdateProgress_Request,
  ) => Promise<Entities_Player_UpdateProgress_Response> =
    params.updateProgress ?? ((vars) => api.post_Player_UpdateProgress(vars));

  let interactionLogged = false;
  let interactionTimerId: number | null = null;
  let gameStartFunction: (() => void) | null = null;
  let gameStopFunction: (() => void) | null = null;

  const gf: GameFrame = {
    symbol,
    scoreboard,
    endScreen,
    practiceMenu,
    changeRoundBtn,
    veil,
    gameStage,

    moduleId: params.moduleId,
    moduleVariant: params.moduleVariant ?? 0,
    preview: params.preview ?? false,
    pwaMode: params.pwaMode ?? false,

    round: 1,
    game: 1,
    score: 0,
    scoreMax: DEFAULT_SCORE_MAX,
    bonus: 0,
    bonusMax: DEFAULT_BONUS_MAX,
    timeOnTask: 0,
    roundsInModule: DEFAULT_ROUNDS_IN_MODULE,
    gamesPerRound: DEFAULT_GAMES_PER_ROUND,

    automateScoreboard: true,
    automateDatabaseUpdates: true,
    displayPoints: false,
    disableNextBtn: false,
    disableMoreBtn: false,

    loadedVars: params.gameDataObject ?? null,

    init,
    initGame,
    startGame,
    endGame,
    nextGame,
    finished,
    getLevel,
    setScore,
    incrementScore,
    incrementBonus,
    updateScoreboard,
    setGameData,
    resetRoundProgress,
    loadSound,
    setBackgroundLoop,
    mute,
  };

  loadFrameworkSounds();
  return gf;

  function loadFrameworkSounds() {
    LoadUtil.loadSound(soundDirectory + 'beep.mp3', 'gameframe-click');
    LoadUtil.loadSound(soundDirectory + 'whoosh.mp3', 'gameframe-whoosh');
    LoadUtil.loadSound(soundDirectory + 'powerup.mp3', 'gameframe-powerup');
  }

  function init() {
    initButton(endScreen.nextBtn, playNextGame);
    initButton(endScreen.moreBtn, navigateMoreGames);
    initButton(practiceMenu.moreBtn, navigateMoreGames);

    UIUtil.createSimpleButton(changeRoundBtn, clickChangeRounds);

    const stage = exportRoot.getStage();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (stage) {
      stage.addEventListener('stagemousedown', () => {
        interactionLogged = true;
      });
    } else {
      logger.warn('GameFrame exportRoot is missing stage reference');
    }
  }

  function initGame(start: () => void, stop?: () => void) {
    gameStartFunction = () => {
      start();
      window.startTimeOnTask();
    };
    gameStopFunction = () => {
      window.stopTimeOnTask();
      stop?.();
    };

    UIUtil.hide(endScreen);
    UIUtil.hide(changeRoundBtn);
    scoreboard.hideProgressPanel();

    for (let i = 1; i <= 4; i++) {
      const btn = practiceMenu[
        ('round' + i + 'Btn') as keyof PracticeMenuSymbol
      ] as GameFrameButton;
      if (i <= gf.roundsInModule) {
        initButton(btn, clickRoundBtn, i);
        btn.x += (4 - gf.roundsInModule) * ROUND_BTN_OFFSET;
      } else {
        UIUtil.hide(btn);
      }
    }

    if (gf.preview) {
      UIUtil.hide(veil);
      return;
    }

    if (!params.playerId) {
      gf.preview = true;
      UIUtil.hide(veil);
      return;
    }

    if (gf.loadedVars != null) {
      onVarsLoad(gf.loadedVars);
      return;
    }

    void getProgress()
      .then(onVarsLoad)
      .catch((err: unknown) => {
        logger.warn('Failed to load progress; entering preview mode', {
          error: String(err),
        });
        gf.preview = true;
        UIUtil.hide(veil);
      });
  }

  function onVarsLoad(vars: GameData | null) {
    gf.loadedVars = vars;

    if (vars != null) {
      gf.round = Calculator.asInt(vars.round);
      gf.game = Calculator.asInt(vars.game);
      gf.extendedData = vars.extendedData;

      if (
        vars.finishedEarning ||
        gf.round < 1 ||
        !params.playerId ||
        params.playerId < 1 ||
        gf.game > gf.gamesPerRound ||
        gf.round > gf.roundsInModule
      ) {
        gf.preview = true;
      }

      if (gf.preview) {
        clickChangeRounds();
      } else {
        UIUtil.hide(practiceMenu);
        startGame();
      }
    } else {
      gf.preview = true;
    }

    UIUtil.hide(veil);
  }

  function startGame() {
    gf.score = 0;
    gf.bonus = 0;
    gf.timeOnTask = 0;
    if (gf.automateScoreboard) updateScoreboard();
    scoreboard.showProgressPanel();
    UIUtil.hide(endScreen, ANIM_MS);
    startInteractionTimer();
    gameStartFunction?.();
  }

  function endGame() {
    stopInteractionTimer();

    gf.bonus = Math.round(gf.bonus + 0.4);

    gameStopFunction?.();
    if (gf.automateDatabaseUpdates) updateDatabase();

    let message = '';
    UIUtil.show(endScreen.stickersText);

    if (!gf.preview) {
      UIUtil.hide(endScreen.nextBtn);
      UIUtil.hide(endScreen.moreBtn);
    } else if (gf.disableMoreBtn) {
      UIUtil.show(practiceMenu.moreBtn, ANIM_MS);
    } else {
      UIUtil.hide(practiceMenu.moreBtn);
    }

    if (gf.displayPoints) {
      message = setPointsMessage();
    } else {
      message = setStickersMessage();
    }

    if (gf.game === gf.gamesPerRound) {
      message = 'You finished this round!';
      LoadUtil.playSound('gameframe-powerup');
    }
    endScreen.messageText.text = message;

    UIUtil.enable(endScreen);
    UIUtil.show(endScreen, ANIM_MS);
  }

  function setPointsMessage(): string {
    let message: string;
    if (gf.score < POINTS_LOW) {
      message = Chance.selectRandom([
        'Keep trying!',
        "Don't give up!",
        'Give it another try.',
        'Keep practicing!',
      ]);
      endScreen.beforeScoreText.text = 'You only scored';
      endScreen.afterScoreText.text = 'points this time.';
    } else if (gf.score < POINTS_MED) {
      message = Chance.selectRandom([
        'Not bad!',
        'Good work.',
        'Keep it up!',
        'Keep practicing!',
      ]);
      endScreen.beforeScoreText.text = 'You scored';
      endScreen.afterScoreText.text = 'points.';
    } else if (gf.score < POINTS_HIGH) {
      message = Chance.selectRandom([
        'Congratulations!',
        'Wow!',
        'Great job!',
        'Excellent!',
        'Well done!',
      ]);
      endScreen.beforeScoreText.text = 'You scored';
      endScreen.afterScoreText.text = 'points!';
    } else {
      message = Chance.selectRandom([
        'Fantastic!',
        'Outstanding!',
        'Amazing!',
        'Superb!',
      ]);
      endScreen.beforeScoreText.text = 'You scored';
      endScreen.afterScoreText.text = 'points!!';
    }
    endScreen.pointsText.text = String(gf.score);
    return message;
  }

  function setStickersMessage(): string {
    endScreen.beforeScoreText.text = '';
    endScreen.pointsText.text = '';
    endScreen.afterScoreText.text = '';

    if (gf.score === 0) {
      endScreen.stickersText.text = '';
      endScreen.beforeScoreText.text = '';
      endScreen.afterScoreText.text = '0 Stickers';
      return Chance.selectRandom([
        'Keep trying!',
        "Don't give up!",
        'Give it another try.',
        'Keep practicing!',
      ]);
    }

    if (gf.score >= STICKERS_LOW_MIN && gf.score <= STICKERS_LOW_MAX) {
      endScreen.beforeScoreText.text = '';
      return Chance.selectRandom([
        'Not bad!',
        'Good work.',
        'Keep it up!',
        'Keep practicing!',
      ]);
    }

    endScreen.afterScoreText.text = Chance.selectRandom([
      'You won!',
      'You did it!',
      'You got it right!',
    ]);
    return Chance.selectRandom([
      'Fantastic!',
      'Outstanding!',
      'Great job!',
      'Excellent!',
      'Well done!',
      'Nice Work!',
    ]);
  }

  function nextGame() {
    UIUtil.disable(endScreen);

    if (gf.preview) {
      if (gf.game < gf.gamesPerRound) {
        gf.game++;
      } else {
        gf.game = 1;
        gf.round++;
        if (gf.round > gf.roundsInModule) {
          gf.round = 0;
        }
      }

      if (gf.round > 0) {
        startGame();
      } else {
        clickChangeRounds();
      }
      return;
    }

    void getProgress()
      .then(onVarsLoad)
      .catch((err: unknown) => {
        logger.warn('Failed to load progress on nextGame', {
          error: String(err),
        });
      });
  }

  function updateDatabase(advanceProgress = true) {
    if (!params.playerId) {
      window.postTimeOnTaskByStatus(true, advanceProgress);
      gf.timeOnTask = 0;
      return;
    }

    const vars = applySaltedHash({
      playerId: params.playerId,
      moduleId: gf.moduleId,
      moduleVariant: gf.moduleVariant,
      round: gf.round,
      game: gf.game,
      score: gf.score,
      bonus: gf.bonus,
      extendedData: gf.extendedData ?? '',
      timeOnTask: gf.timeOnTask,
      advanceProgress,
    });

    if (!gf.preview) {
      void updateProgress(vars).then(onDatabasePost);
    }

    window.postTimeOnTaskByStatus(gf.preview, advanceProgress);

    gf.timeOnTask = 0;
  }

  function onDatabasePost(result: Entities_Player_UpdateProgress_Response) {
    endScreen.stickersText.text = '';

    const stickers = Calculator.asInt(result.stickers);
    if (!gf.disableNextBtn) UIUtil.show(endScreen.nextBtn, ANIM_MS);
    if (!gf.disableMoreBtn) UIUtil.show(endScreen.moreBtn, ANIM_MS);
    if (stickers > 0) {
      endScreen.stickersText.text =
        'You earned ' + stickers + (stickers > 1 ? ' stickers' : ' sticker');
      UIUtil.show(endScreen.stickersText, ANIM_MS);
    }
  }

  function resetRoundProgress() {
    gf.game = 0;
    updateDatabase();
  }

  function setGameData(vars: GameData | null) {
    gf.loadedVars = vars;
  }

  function finished(): boolean {
    return gf.score >= gf.scoreMax;
  }

  function getLevel(): number {
    return Math.floor((6 * gf.score) / gf.scoreMax) + 1;
  }

  function updateScoreboard() {
    scoreboard.updateRound(gf.round);
    scoreboard.updateGame(gf.game);
    scoreboard.updateProgress(gf.score / gf.scoreMax);
  }

  function setScore(score: number) {
    gf.score = Calculator.bound(score, 0, gf.scoreMax);
    if (gf.automateScoreboard) updateScoreboard();
  }

  function incrementScore(by = 1) {
    setScore(gf.score + Calculator.asInt(by));
  }

  function incrementBonus(by: number) {
    gf.bonus += by;
    if (gf.bonusMax > 0 && gf.bonus > gf.bonusMax) gf.bonus = gf.bonusMax;
    scoreboard.updateBonus(gf.bonus);
  }

  function loadSound(src: string, id: string) {
    LoadUtil.loadSound(soundDirectory + src, id);
  }

  function setBackgroundLoop(src: string) {
    LoadUtil.startLoop(soundDirectory + 'loops/' + src);
  }

  function mute() {
    scoreboard.mute();
  }

  /* Internal handlers */

  function initButton(
    btn: GameFrameButton,
    onClick: (e: TargettedEvent<GameFrameButton>) => void,
    value?: unknown,
  ) {
    UIUtil.createButton<GameFrameButton>(
      btn,
      onClick,
      value,
      onRoundButtonOver,
      onRoundButtonOut,
    );
    UIUtil.hide(btn.btnHover);
  }

  function clickChangeRounds() {
    LoadUtil.playSoundAtVolume('gameframe-whoosh', 0.5);
    gameStopFunction?.();
    UIUtil.show(practiceMenu, ANIM_MS);
    UIUtil.hide(changeRoundBtn);
    scoreboard.hideProgressPanel();
  }

  function clickRoundBtn(e: TargettedEvent<GameFrameButton>) {
    LoadUtil.playSoundAtVolume('gameframe-click', 0.5);
    const btn = UIUtil.getEventTarget(e);
    gf.round = btn.value as number;
    gf.game = 1;
    UIUtil.show(changeRoundBtn);
    UIUtil.hide(practiceMenu, ANIM_MS);
    startGame();
  }

  function onRoundButtonOver(e: TargettedEvent<{ bg: createjs.MovieClip }>) {
    UIUtil.setFrame(UIUtil.getEventTarget(e).bg, 2);
  }

  function onRoundButtonOut(e: TargettedEvent<{ bg: createjs.MovieClip }>) {
    UIUtil.setFrame(UIUtil.getEventTarget(e).bg, 1);
  }

  function playNextGame() {
    LoadUtil.playSoundAtVolume('gameframe-click', 0.5);
    nextGame();
  }

  function navigateMoreGames() {
    LoadUtil.playSoundAtVolume('gameframe-click', 0.5);
    if (params.hubUrl) UIUtil.navigate(params.hubUrl);
  }

  function startInteractionTimer() {
    if (interactionTimerId != null) return;
    interactionTimerId = window.setInterval(() => {
      if (interactionLogged) {
        gf.timeOnTask += INTERACTION_TICK_SECONDS;
        interactionLogged = false;
      }
    }, INTERACTION_TICK_MS);
  }

  function stopInteractionTimer() {
    if (interactionTimerId != null) {
      window.clearInterval(interactionTimerId);
      interactionTimerId = null;
    }
  }
}

function applySaltedHash(
  vars: Omit<Entities_Player_UpdateProgress_Request, 'salt' | 'hash'>,
): Entities_Player_UpdateProgress_Request {
  // Get the salt and then call the method with that name
  const salt = window.salter();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  (window as any)[salt](vars);
  return vars as Entities_Player_UpdateProgress_Request;
}
